import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink, Minus, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { api } from "@/api/client";
import { createPoller, type Poller } from "@/lib/poller";
import type { RollupMetricKey, RollupMover, RollupReport } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/ui/error-state";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { cn } from "@/lib/utils";

const METRIC_LABELS: Record<RollupMetricKey, string> = {
  visibility: "Visibility",
  sentiment: "Sentiment",
  overall_score: "Score",
};
const METRICS: RollupMetricKey[] = ["visibility", "sentiment", "overall_score"];

type LoadState = "loading" | "ready" | "not-found" | "error";

function Delta({ value }: { value: number | null }) {
  const dir = value === null ? "none" : value > 0.5 ? "up" : value < -0.5 ? "down" : "flat";
  const Icon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ml-1.5",
      dir === "up" && "text-green-600",
      dir === "down" && "text-red-500",
      (dir === "flat" || dir === "none") && "text-slate-300",
    )}>
      <Icon className="w-3 h-3" />
      {value === null ? "—" : `${value > 0 ? "+" : ""}${value.toFixed(1)}`}
    </span>
  );
}

const fmt = (n: number | null) => (n === null ? "—" : n.toFixed(1));

interface RollupDetailViewProps {
  rollupId: number;
  onBack: () => void;
}

export function RollupDetailView({ rollupId, onBack }: RollupDetailViewProps) {
  const [rollup, setRollup] = useState<RollupReport | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [error, setError] = useState("");
  const pollerRef = useRef<Poller | null>(null);

  const fetchDetail = async () => {
    try {
      const data = await api.rollups.get(rollupId);
      setRollup(data);
      setLoadState("ready");
    } catch (err: any) {
      const message = err?.message || "Unable to load rollup";
      setError(message);
      setLoadState(message.toLowerCase().includes("not found") ? "not-found" : "error");
    }
  };

  useEffect(() => {
    setLoadState("loading");
    fetchDetail();
    return () => { pollerRef.current?.cancel(); pollerRef.current = null; };
  }, [rollupId]);

  // Poll while the narrative is still being written.
  useEffect(() => {
    if (rollup?.status !== "generating") return;
    pollerRef.current?.cancel();
    pollerRef.current = createPoller(
      async () => {
        const data = await api.rollups.get(rollupId);
        setRollup(data);
        return data.status !== "generating";
      },
      { intervalMs: 3000, timeoutMs: 120000 },
    );
    return () => { pollerRef.current?.cancel(); pollerRef.current = null; };
  }, [rollup?.status, rollupId]);

  const handleDelete = async () => {
    if (!confirm("Delete this rollup?")) return;
    await api.rollups.delete(rollupId);
    onBack();
  };

  if (loadState === "loading") return <Spinner message="Loading rollup..." size="lg" />;
  if (loadState === "not-found") return <p className="text-sm text-slate-500">Rollup not found.</p>;
  if (loadState === "error" || !rollup) {
    return <ErrorState message={error} onRetry={() => { setLoadState("loading"); fetchDetail(); }} />;
  }

  const snap = rollup.data;
  const shareUrl = `/rollups/${rollup.slug}`;
  const maxShare = snap ? Math.max(...snap.share_of_voice.map(s => s.share), 1) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-brand-950 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          All Rollups
        </button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-600" onClick={handleDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button variant="secondary" size="sm" asChild>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              Shareable view
            </a>
          </Button>
        </div>
      </div>

      {/* Headline + narrative */}
      <Card className="overflow-hidden">
        <CardContent className="py-6 px-6">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="winner">{rollup.name}</Badge>
            {snap && <span className="text-[12px] text-slate-400">{snap.total_runs} runs analyzed</span>}
          </div>
          {rollup.headline && <h2 className="text-[22px] font-bold text-brand-950 leading-snug mb-2">{rollup.headline}</h2>}
          {rollup.exec_summary ? (
            <p className="text-[14px] text-slate-600 leading-relaxed max-w-[680px]">{rollup.exec_summary}</p>
          ) : rollup.status === "generating" ? (
            <p className="text-[13px] text-slate-400">Writing the executive recap…</p>
          ) : (
            <p className="text-[13px] text-slate-400">Narrative unavailable — the data below is complete.</p>
          )}
        </CardContent>
      </Card>

      {snap && (
        <>
          {/* Brand performance table */}
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Brand Performance</CardTitle>
              <CardDescription>Average per metric, with change vs {snap.prev_period_label}</CardDescription>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  {METRICS.map(m => <TableHead key={m} className="text-right">{METRIC_LABELS[m]}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {snap.brands.map(b => (
                  <TableRow key={b.brand_id}>
                    <TableCell>
                      <span className="font-medium text-brand-950">{b.brand_name}</span>
                      {b.is_primary && <Badge variant="winner" className="ml-2">Primary</Badge>}
                    </TableCell>
                    {METRICS.map(m => (
                      <TableCell key={m} className="text-right">
                        <span className="font-semibold text-brand-950 tabular-nums">{fmt(b.metrics[m].avg)}</span>
                        <Delta value={b.metrics[m].delta} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Share of voice */}
            <Card>
              <CardHeader>
                <CardTitle>Share of Voice</CardTitle>
                <CardDescription>Visibility-weighted presence this period</CardDescription>
              </CardHeader>
              <CardContent>
                {snap.share_of_voice.length === 0 ? (
                  <p className="py-6 text-center text-[13px] text-slate-400">No visibility data this period.</p>
                ) : (
                  <div className="space-y-3">
                    {snap.share_of_voice.slice(0, 8).map(s => (
                      <div key={s.brand_name}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={cn("text-[13px] font-medium truncate", s.tracked ? "text-brand-950" : "text-slate-500")}>
                              {s.brand_name}
                            </span>
                            {!s.tracked && <Badge variant="muted">untracked</Badge>}
                          </div>
                          <span className="text-[12px] font-semibold text-brand-950 tabular-nums">{s.share.toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(s.share / maxShare) * 100}%`, backgroundColor: s.tracked ? "#4f46e5" : "#cbd5e1" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Movers */}
            <Card>
              <CardHeader>
                <CardTitle>Movers</CardTitle>
                <CardDescription>Largest changes vs {snap.prev_period_label}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <MoversList title="Gainers" movers={snap.gainers} positive />
                  <MoversList title="Decliners" movers={snap.decliners} positive={false} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* New names */}
          <Card>
            <CardHeader>
              <CardTitle>New Names in Answers</CardTitle>
              <CardDescription>Untracked brands that first appeared this period</CardDescription>
            </CardHeader>
            <CardContent>
              {snap.new_brands.length === 0 ? (
                <p className="py-6 text-center text-[13px] text-slate-400">No new brands surfaced this period.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {snap.new_brands.slice(0, 10).map(n => (
                    <div key={n.brand_name} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                      <p className="text-[13px] font-medium text-brand-950">{n.brand_name}</p>
                      <p className="text-[11px] text-slate-400">
                        {n.appearances} mention{n.appearances === 1 ? "" : "s"}
                        {n.avg_sentiment !== null ? ` · sentiment ${n.avg_sentiment.toFixed(0)}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function MoversList({ title, movers, positive }: { title: string; movers: RollupMover[]; positive: boolean }) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-400 mb-2">{title}</h3>
      {movers.length === 0 ? (
        <p className="text-[12px] text-slate-300">None</p>
      ) : (
        <ul className="space-y-1.5">
          {movers.map((m, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="text-brand-950 truncate">
                {m.brand_name} <span className="text-slate-400 text-[11px]">{METRIC_LABELS[m.metric]}</span>
              </span>
              <span className={cn("font-semibold tabular-nums shrink-0", positive ? "text-green-600" : "text-red-500")}>
                {m.delta > 0 ? "+" : ""}{m.delta.toFixed(1)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
